import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

function PasswordInput({ value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input className="h-11 pr-10" type={show ? "text" : "password"} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid={`${testid}-toggle`} aria-label={show ? "Hide password" : "Show password"}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCur(""); setNext(""); setConfirm(""); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const submit = async () => {
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("New password and confirmation do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(data.message || "Password changed successfully");
      close(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Change password</DialogTitle>
          <DialogDescription>Enter your current password and choose a new one. You'll keep using the same login ID.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Current password</Label>
            <PasswordInput value={cur} onChange={setCur} testid="current-password-input" placeholder="Current password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">New password</Label>
            <PasswordInput value={next} onChange={setNext} testid="new-password-input" placeholder="At least 6 characters" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Confirm new password</Label>
            <PasswordInput value={confirm} onChange={setConfirm} testid="confirm-password-input" placeholder="Re-enter new password" />
          </div>
          <Button disabled={busy || !cur || !next || !confirm} onClick={submit} className="h-11 w-full rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC] btn-press" data-testid="change-password-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
